import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import CustomModal from "../../basics/interface/CustomModal"
import InputV2 from "../../basics/input/InputV2"
import FilteredSelectV2 from "../../basics/input/FilteredSelectV2"
import CouverturesCarte from "../couvertures/CouverturesCarte"
import { completerMission } from "../../../store/actions/depannageDispatchActions"
import {
	fetchAutreEntites,
	fetchMarques,
	fetchVehModeles,
} from "../../../store/actions/autreActions"
import SelectV2 from "../../basics/input/SelectV2"
import { COLORS } from "../../../utilities/Constants"
import { DEPANNAGE_TYPE_APPEL_PRIVE } from "../../../utilities/Addons"
import { SliderPhotos, fichiersMission } from "./PhotosMission"
import { useEtroit } from "../couvertures/couverturesUi"
import { generalStyles as gs } from "../../styles/styles"

// GE-62 (B5) : « Compléter » une mission en cours (30-59) depuis le détail
// En cours — décision Jordan 09.10 : client, véhicule, n° de dossier, info
// dépanneur, couverture. La destination reste au dépanneur.
// Écriture en delta + base_version : seuls les champs modifiés partent ; un
// champ écrit entre-temps par quelqu'un d'autre revient en conflit (valeur
// actuelle + auteur), à trancher champ par champ.

const CHAMPS = [
	"nom",
	"prenom",
	"telephone",
	"email",
	"adresse_client_0",
	"adresse_client_1",
	"plaques",
	"marque",
	"modele",
	"numero_chassis",
	"num_dossier",
	"info_depanneur",
	// Revue phase B (décision 4) : l'assistance se complète aussi.
	"assistance_entite",
	// GE-66 D3 / J6 : coche « client sous contrat » (privé seulement,
	// figée par le serveur dès qu'un encaissement existe).
	"prive_sous_contrat",
]

// Valeurs du formulaire en texte (l'assistance : id en texte, "" = aucune).
const valeursDe = (dm) =>
	CHAMPS.reduce((acc, c) => {
		const v = dm[c]
		if (c === "assistance_entite")
			return { ...acc, [c]: v ? String(v) : "" }
		if (c === "prive_sous_contrat") return { ...acc, [c]: !!v }
		return { ...acc, [c]: v == null ? "" : v }
	}, {})

// Valeur envoyée au serveur (assistance : id entier, 0 = aucune).
const versServeur = (c, v) =>
	c === "assistance_entite" ? (v ? parseInt(v) : 0) : v

// GE-66 D3 : la coche « sous contrat » ne change plus après un encaissement.
const encaissee = (dm) => (dm.transactionsv2 || []).length > 0

// États où le dépanneur reçoit la mise à jour (30-59).
const EN_COURS_MIN = 30
const EN_COURS_MAX = 60

// Champ hoisté hors du composant (sinon perte de focus à chaque frappe).
// `affiche` : valeur lisible du conflit (revue 5 : le nom de l'assistance,
// pas son id).
const Champ = ({
	libelle,
	conflit,
	onGarder,
	onReprendre,
	children,
	affiche = (v) => v,
}) => (
	<div style={s.field}>
		<span style={s.label}>{libelle}</span>
		{children}
		{conflit && (
			<div style={s.conflit} role="alert">
				<div>
					{conflit.message} Valeur actuelle :{" "}
					<strong>{affiche(conflit.valeur) || "—"}</strong>
				</div>
				<div style={{ display: "flex", gap: 8, marginTop: 4 }}>
					<button type="button" style={s.lien} onClick={onReprendre}>
						Reprendre la valeur actuelle
					</button>
					<button type="button" style={s.lien} onClick={onGarder}>
						Garder la mienne
					</button>
				</div>
			</div>
		)}
	</div>
)

// `sortie` : la mission a quitté En cours (état affiché dans le bandeau) —
// lecture seule, plus d'enregistrement possible ici.
const CompleterMission = ({ dm, onClose, sortie = false }) => {
	const dispatch = useDispatch()
	const marques = useSelector((state) => state.autreInfo.marques)
	const assistances = useSelector((state) => state.autreInfo.assistances)
	const modeles = useSelector((state) => state.autreInfo.modeles)
	// Valeurs lues à l'ouverture (référence du delta) et version de base.
	const [initiales, setInitiales] = useState(() => valeursDe(dm))
	const [valeurs, setValeurs] = useState(() => valeursDe(dm))
	const [baseVersion, setBaseVersion] = useState(dm.version || 0)
	const [conflits, setConflits] = useState({})
	const [confirmer, setConfirmer] = useState(false)
	const [enCours, setEnCours] = useState(false)
	const [fait, setFait] = useState(false)
	const [marqueId, setMarqueId] = useState(null)
	// Slider de photos (Jordan 09.10) : photo affichée et, par photo, rotation
	// / zoom locaux — gardés quand on bascule « Champs / Photos ».
	// Revue phase B (🟡 J) : photo suivie par id (une carte grise arrivée en
	// cours de route se trie en tête sans changer la photo affichée).
	const [photoId, setPhotoId] = useState(null)
	const [vues, setVues] = useState({})
	const [erreur, setErreur] = useState("")
	const [confirmFermer, setConfirmFermer] = useState(false)
	// Revue 5 : « le dépanneur reçoit la mise à jour » d'après l'état RENVOYÉ.
	const [faitChezDepanneur, setFaitChezDepanneur] = useState(false)
	const minuteur = useRef(null)
	const derniereVersion = useRef(dm.version || 0)
	// Revue 6 (D-1) : copie la plus récente reçue (prop, WS / sondage).
	const dmRef = useRef(dm)
	dmRef.current = dm
	useEffect(() => () => clearTimeout(minuteur.current), [])
	const etroit = useEtroit(900)
	const [volet, setVolet] = useState("champs")

	const modifies = CHAMPS.filter((c) => valeurs[c] !== initiales[c])

	useEffect(() => {
		if (!marques || marques.length === 0) dispatch(fetchMarques())
		if (!assistances || assistances.length === 0)
			dispatch(fetchAutreEntites())
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	useEffect(() => {
		const m = (marques || []).find(
			(mq) =>
				mq.nom.toLowerCase() ===
				(valeurs.marque || "").trim().toLowerCase(),
		)
		if (m && m.id !== marqueId) {
			setMarqueId(m.id)
			dispatch(fetchVehModeles(m.id))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [marques])

	// La mission a bougé pendant la saisie (poll, B3, relecture après un
	// 409) : les champs que je n'ai pas touchés suivent ; les miens restent
	// (le serveur tranchera).
	const suivre = (mission) => {
		// Revue phase B (🟠4) : une copie plus ancienne que la dernière vue
		// (réponses dans le désordre) est ignorée.
		if ((mission.version || 0) < derniereVersion.current) return
		derniereVersion.current = mission.version || 0
		const neuves = valeursDe(mission)
		setInitiales((avant) => {
			const suite = { ...avant }
			CHAMPS.forEach((c) => {
				if (valeurs[c] === avant[c]) suite[c] = neuves[c]
			})
			return suite
		})
		setValeurs((avant) => {
			const suite = { ...avant }
			CHAMPS.forEach((c) => {
				if (avant[c] === initiales[c]) suite[c] = neuves[c]
			})
			return suite
		})
	}
	useEffect(() => {
		suivre(dm)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dm.version])

	// Revue passe 3 (🟡 C) : encaissement arrivé pendant la saisie → la coche
	// (figée) reprend la valeur serveur, sinon chaque Enregistrer la renvoie
	// et prend un 400.
	const estEncaissee = encaissee(dm)
	const sousContratServeur = !!dm.prive_sous_contrat
	useEffect(() => {
		if (!estEncaissee) return
		const c = "prive_sous_contrat"
		setValeurs((avant) => ({ ...avant, [c]: sousContratServeur }))
		setInitiales((avant) => ({ ...avant, [c]: sousContratServeur }))
		fermerConflit(c)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [estEncaissee, sousContratServeur])

	const set = (c) => (e) => {
		const v = e && e.target ? e.target.value : e
		setValeurs((avant) => ({ ...avant, [c]: v }))
		setConfirmer(false)
		setConfirmFermer(false)
		setErreur("")
	}

	const fermerConflit = (c) =>
		setConflits((avant) => {
			const suite = { ...avant }
			delete suite[c]
			return suite
		})
	const reprendre = (c) => {
		const brute = conflits[c].valeur
		const v =
			c === "prive_sous_contrat"
				? !!brute
				: brute == null
					? ""
					: String(brute)
		setValeurs((avant) => ({ ...avant, [c]: v }))
		setInitiales((avant) => ({ ...avant, [c]: v }))
		fermerConflit(c)
	}
	const garder = (c) => fermerConflit(c)

	// Revue phase B (🟡 J) : quitter avec des modifications non enregistrées
	// demande une confirmation (switch sur le bouton).
	const fermer = () => {
		if (enCours) return
		if (modifies.length > 0 && !confirmFermer) {
			setConfirmFermer(true)
			return
		}
		onClose()
	}

	const enregistrer = () => {
		if (enCours || modifies.length === 0 || sortie) return
		if (Object.keys(conflits).length > 0) return
		if (!confirmer) {
			setConfirmer(true)
			return
		}
		setConfirmFermer(false)
		setEnCours(true)
		const delta = modifies.reduce(
			(acc, c) => ({ ...acc, [c]: versServeur(c, valeurs[c]) }),
			{},
		)
		dispatch(completerMission(dm.id, baseVersion, delta)).then((res) => {
			setEnCours(false)
			setConfirmer(false)
			if (res.ok) {
				setFait(true)
				const v = res.mission.version || 0
				// Revue 5 : réponse appliquée seulement si c'est la plus
				// fraîche (un sondage / WS a pu livrer une version plus
				// récente pendant le vol) ; sinon l'affichage reste, la base
				// suit la plus récente.
				if (v >= derniereVersion.current) {
					setInitiales(valeursDe(res.mission))
					setValeurs(valeursDe(res.mission))
					setBaseVersion(v)
					derniereVersion.current = v
				} else {
					// Revue 6 (D-1) : les champs ENVOYÉS reprennent la valeur de
					// la copie la plus récente (déjà suivie) — sinon ils restaient
					// « modifiés » sur l'ancienne base et un nouvel Enregistrer
					// écrasait la saisie du dépanneur sans 409.
					const recentes = valeursDe(dmRef.current)
					const envoyes = Object.keys(delta)
					const aligner = (avant) => {
						const suite = { ...avant }
						envoyes.forEach((c) => {
							suite[c] = recentes[c]
						})
						return suite
					}
					setInitiales(aligner)
					setValeurs(aligner)
					setBaseVersion(derniereVersion.current)
				}
				setFaitChezDepanneur(
					res.mission.etat >= EN_COURS_MIN &&
						res.mission.etat < EN_COURS_MAX,
				)
				clearTimeout(minuteur.current)
				minuteur.current = setTimeout(() => setFait(false), 1500)
				return
			}
			if (!res.conflits) {
				// Revue phase B (🟠3) : 409 sans champ, 400… — dit, pas avalé.
				// Revue 5 : « rien n'a été enregistré » seulement si c'est sûr
				// (4xx / 500 : transaction annulée) ; réseau, 502, 504 : la
				// requête a pu aboutir.
				const sur =
					(res.httpStatus >= 400 && res.httpStatus < 500) ||
					res.httpStatus === 500
				setErreur(
					`${res.message || "L'enregistrement a échoué."} ${
						sur
							? "Rien n'a été enregistré."
							: "L'enregistrement a peut-être abouti : rechargez la mission avant de réessayer."
					}`,
				)
				return
			}
			setConflits(res.conflits)
			setVolet("champs")
			// Revue 5 : base relevée sur la mission RELUE (jamais sur les
			// seules versions des conflits : un autre champ écrit entre-temps
			// serait écrasé sans conflit).
			if (res.mission) {
				suivre(res.mission)
				setBaseVersion(res.mission.version || 0)
			} else {
				// Revue 6 (D-2) : relecture en échec (réseau) — la base n'a
				// pas été relevée : le dire, sinon 409 en boucle sans
				// explication.
				setErreur(
					"Conflit : la mission a changé et n'a pas pu être relue. Fermez puis rouvrez la mission avant de réessayer.",
				)
			}
		})
	}

	const champ = (c, libelle, extra = {}) => (
		<Champ
			libelle={libelle}
			conflit={conflits[c]}
			onGarder={() => garder(c)}
			onReprendre={() => reprendre(c)}
		>
			<InputV2
				type="text"
				value={valeurs[c]}
				onChange={set(c)}
				disabled={enCours || sortie}
				style={{
					...s.input,
					...(conflits[c]
						? { border: `1px solid ${COLORS.warning}` }
						: null),
				}}
				{...extra}
			/>
		</Champ>
	)

	const fichiers = fichiersMission(dm)
	const nbConflits = Object.keys(conflits).length
	const indexPhoto = Math.max(
		0,
		fichiers.findIndex((f) => f.id === photoId),
	)
	const setIndexPhoto = (i) => fichiers[i] && setPhotoId(fichiers[i].id)

	return (
		<CustomModal
			title="Compléter la mission"
			subtitle={[`M${dm.id}`, dm.plaques].filter(Boolean).join(" · ")}
			maxWidth={etroit ? 760 : 1240}
			onClose={fermer}
			showCloseButton={!enCours}
			footerLeft={
				erreur ? (
					<span style={{ fontSize: 12.5, color: COLORS.danger }}>
						{erreur}
					</span>
				) : confirmFermer ? (
					<span style={{ fontSize: 12.5, color: COLORS.warning }}>
						Modifications non enregistrées : cliquer à nouveau pour
						quitter sans enregistrer.
					</span>
				) : nbConflits > 0 ? (
					<span style={{ fontSize: 12.5, color: COLORS.warning }}>
						{nbConflits === 1
							? "1 champ à trancher"
							: `${nbConflits} champs à trancher`}
						{
							" — rien n'a été enregistré : tranchez puis enregistrez."
						}
					</span>
				) : fait ? (
					<span style={{ fontSize: 12.5, color: COLORS.success }}>
						{faitChezDepanneur
							? "✓ Enregistré — le dépanneur reçoit la mise à jour"
							: "✓ Enregistré"}
					</span>
				) : modifies.length > 0 ? (
					<span style={{ fontSize: 12.5, color: COLORS.textMuted }}>
						{modifies.length === 1
							? "1 champ modifié"
							: `${modifies.length} champs modifiés`}
					</span>
				) : null
			}
			buttons={[
				{
					title: confirmFermer
						? "Quitter sans enregistrer ?"
						: "Retour",
					styleName: "secondary",
					onClick: fermer,
					disabled: enCours,
				},
				{
					title: confirmer ? "Confirmer ?" : "Enregistrer",
					styleName: "primary",
					onClick: enregistrer,
					disabled:
						enCours ||
						sortie ||
						modifies.length === 0 ||
						nbConflits > 0,
					loading: enCours,
				},
			]}
		>
			{sortie && (
				<div style={s.sortie} role="status">
					{typeof sortie === "string"
						? sortie
						: "Mission sortie d'En cours : plus de modification ici."}
				</div>
			)}
			{etroit && (
				<div style={{ ...gs.segmented, marginBottom: 16 }}>
					{[
						["champs", "Champs"],
						["photos", `Photos (${fichiers.length})`],
					].map(([cle, libelle]) => (
						<button
							key={`volet_${cle}`}
							type="button"
							style={gs.segment(volet === cle)}
							onClick={() => setVolet(cle)}
						>
							{libelle}
						</button>
					))}
				</div>
			)}
			<div style={etroit ? null : s.split}>
				<div
					style={{
						...(etroit ? null : s.colonneChamps),
						display:
							etroit && volet !== "champs" ? "none" : "block",
					}}
				>
					<div
						style={{
							...s.section,
							borderTop: "none",
							paddingTop: 0,
						}}
					>
						Client
					</div>
					<div style={s.row2}>
						{champ("nom", "Nom")}
						{champ("prenom", "Prénom")}
					</div>
					<div style={s.row2}>
						{champ("telephone", "Téléphone", { type: "tel" })}
						{champ("email", "E-mail", { type: "email" })}
					</div>
					<div style={s.row2}>
						{champ("adresse_client_0", "Adresse — rue et numéro")}
						{champ("adresse_client_1", "Adresse — NPA, localité")}
					</div>

					<div style={s.section}>Véhicule</div>
					<div>
						<div>
							<div style={s.row2}>
								{champ("plaques", "Plaques")}
								{champ("numero_chassis", "N° de châssis")}
							</div>
							<div style={s.row2}>
								<Champ
									libelle="Marque"
									conflit={conflits.marque}
									onGarder={() => garder("marque")}
									onReprendre={() => reprendre("marque")}
								>
									<FilteredSelectV2
										options={marques || []}
										defaultKey="nom"
										optionKeys="nom"
										value={valeurs.marque}
										onChange={(texte) => {
											set("marque")(texte)
											if (marqueId) {
												setMarqueId(null)
												dispatch(fetchVehModeles(0))
											}
										}}
										onSelect={(m) => {
											if (!m) return
											set("marque")(m.nom)
											setMarqueId(m.id)
											dispatch(fetchVehModeles(m.id))
										}}
										placeholder="Marque"
										disabled={enCours || sortie}
										inputStyle={s.input}
									/>
								</Champ>
								<Champ
									libelle="Modèle"
									conflit={conflits.modele}
									onGarder={() => garder("modele")}
									onReprendre={() => reprendre("modele")}
								>
									<FilteredSelectV2
										options={marqueId ? modeles || [] : []}
										defaultKey="nom"
										optionKeys="nom"
										value={valeurs.modele}
										onChange={(texte) =>
											set("modele")(texte)
										}
										onSelect={(m) =>
											m && set("modele")(m.nom)
										}
										placeholder="Modèle"
										disabled={enCours || sortie}
										inputStyle={s.input}
									/>
								</Champ>
							</div>
						</div>
					</div>

					<div style={s.section}>Dossier</div>
					<div style={s.row2}>
						{champ("num_dossier", "N° de dossier")}
						<Champ
							libelle="Assistance"
							conflit={conflits.assistance_entite}
							onGarder={() => garder("assistance_entite")}
							onReprendre={() => reprendre("assistance_entite")}
							affiche={(v) =>
								(
									(assistances || []).find(
										(a) => String(a.id) === String(v),
									) || {}
								).label || v
							}
						>
							<SelectV2
								value={valeurs.assistance_entite}
								onChange={set("assistance_entite")}
								disabled={enCours || sortie}
								style={s.input}
							>
								<option value="">— Aucune —</option>
								{(assistances || []).map((a) => (
									<option
										key={`assistance_${a.id}`}
										value={String(a.id)}
									>
										{a.label}
									</option>
								))}
							</SelectV2>
						</Champ>
					</div>
					{dm.appel_type === DEPANNAGE_TYPE_APPEL_PRIVE && (
						<Champ
							libelle="Tarif privé"
							conflit={conflits.prive_sous_contrat}
							onGarder={() => garder("prive_sous_contrat")}
							onReprendre={() => reprendre("prive_sous_contrat")}
							affiche={(v) => (v ? "cochée" : "non cochée")}
						>
							<label style={s.coche}>
								<input
									type="checkbox"
									checked={valeurs.prive_sous_contrat}
									disabled={
										enCours || sortie || encaissee(dm)
									}
									onChange={(e) =>
										set("prive_sous_contrat")(
											e.target.checked,
										)
									}
								/>
								Client sous contrat dépannage
								<span style={s.cocheAide}>
									{encaissee(dm)
										? "— figée : un encaissement est enregistré"
										: "— même tarif, rien à encaisser sur place"}
								</span>
							</label>
						</Champ>
					)}
					<Champ
						libelle="Info dépanneur"
						conflit={conflits.info_depanneur}
						onGarder={() => garder("info_depanneur")}
						onReprendre={() => reprendre("info_depanneur")}
					>
						<textarea
							disabled={enCours || sortie}
							value={valeurs.info_depanneur}
							onChange={set("info_depanneur")}
							rows={3}
							style={s.textarea}
						/>
					</Champ>

					<div style={s.section}>Couverture</div>
					<CouverturesCarte
						missionId={dm.id}
						compact
						sousTitre={[`Mission M${dm.id}`, dm.plaques]
							.filter(Boolean)
							.join(" · ")}
					/>
				</div>
				<div
					style={{
						...(etroit ? s.colonnePhotosEtroite : s.colonnePhotos),
						display: etroit && volet !== "photos" ? "none" : "flex",
					}}
				>
					<SliderPhotos
						fichiers={fichiers}
						index={indexPhoto}
						setIndex={setIndexPhoto}
						vues={vues}
						setVues={setVues}
					/>
				</div>
			</div>
		</CustomModal>
	)
}

const s = {
	coche: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		fontSize: 13,
		color: COLORS.text,
		cursor: "pointer",
		minHeight: 38,
	},
	cocheAide: { fontSize: 12, color: COLORS.textMuted },
	section: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: COLORS.textLight,
		margin: "6px 0 12px",
		paddingTop: 12,
		borderTop: `1px solid ${COLORS.border}`,
	},
	field: { marginBottom: 14, display: "flex", flexDirection: "column" },
	row2: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
		gap: 14,
	},
	label: {
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.textMuted,
		marginBottom: 6,
	},
	input: {
		width: "100%",
		height: 38,
		padding: "0 12px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		fontSize: 14,
		color: COLORS.text,
		fontFamily: "inherit",
		backgroundColor: COLORS.surface,
		boxSizing: "border-box",
	},
	textarea: {
		width: "100%",
		padding: "10px 12px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		fontSize: 14,
		color: COLORS.text,
		fontFamily: "inherit",
		backgroundColor: COLORS.surface,
		boxSizing: "border-box",
		resize: "vertical",
	},
	// Écran large : coupé verticalement, champs à gauche, photos à droite
	// (collées en haut pendant que les champs défilent).
	split: { display: "flex", gap: 24, alignItems: "flex-start" },
	colonneChamps: { flex: "1 1 0", minWidth: 0 },
	colonnePhotos: {
		flex: "1 1 0",
		minWidth: 0,
		position: "sticky",
		top: 0,
		height: "min(640px, calc(100vh - 260px))",
		flexDirection: "column",
	},
	colonnePhotosEtroite: {
		height: "min(560px, calc(100vh - 300px))",
		flexDirection: "column",
	},
	sortie: {
		marginBottom: 14,
		padding: "8px 12px",
		borderRadius: 8,
		background: COLORS.warningLight,
		border: `1px solid ${COLORS.warning}`,
		fontSize: 12.5,
		color: COLORS.text,
	},
	conflit: {
		marginTop: 6,
		padding: "8px 10px",
		borderRadius: 8,
		background: COLORS.warningLight,
		border: `1px solid ${COLORS.warning}`,
		fontSize: 12.5,
		color: COLORS.text,
	},
	lien: {
		padding: 0,
		border: "none",
		background: "none",
		color: COLORS.accent,
		fontSize: 12.5,
		cursor: "pointer",
		textDecoration: "underline",
	},
}

export default CompleterMission
